import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ArrowLeft, GitBranch, Pencil} from "lucide-react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {useTranslation} from "../../shared/i18n";
import {createPlace, loadPlace, loadPlaceProperties, updatePlace} from "./api";
import {BranchEditorPage} from "./BranchEditorPage";
import {PlaceForm} from "./components/PlaceForm";
import {placeToFormValues, toPlaceFormData} from "./contracts";
import {useActiveAdminCountryScope} from "./useActiveAdminCountryScope";
import "./organizations.css";

const PLATFORM_ROLES = new Set([ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR]);

const emptyValues = () => ({
    ...placeToFormValues(),
    password: "",
    logo_exists: false,
    logo_change: false,
});

const valuesFromPlace = (place) => ({
    ...placeToFormValues(place),
    password: "",
    logo_exists: Boolean(place.logo_exists),
    logo_change: false,
});

const validatePlace = (values, {isCreate, existingMedia, t}) => {
    const required = [
        ["name", t("orgs.placeEditor.missing.name")],
        ["head_name", t("orgs.placeEditor.missing.head")],
        ["phone_number", t("orgs.placeEditor.missing.phone")],
        ["login", t("orgs.placeEditor.missing.login")],
        ["bin_number", t("orgs.placeEditor.missing.bin")],
        ["account_number", t("orgs.placeEditor.missing.account")],
        ["off_percent", t("orgs.placeEditor.missing.commission")],
        ["city", t("orgs.placeEditor.missing.city")],
        ["category", t("orgs.placeEditor.missing.category")],
    ];
    const missing = required.filter(([field]) => String(values[field] ?? "").trim() === "");
    if (isCreate && !String(values.password || "").trim()) missing.push(["password", t("orgs.placeEditor.missing.password")]);
    if (!values.agreement && !existingMedia.agreement) missing.push(["agreement", t("orgs.placeEditor.missing.agreement")]);
    if (!values.background && !existingMedia.background) missing.push(["background", t("orgs.placeEditor.missing.background")]);
    if (missing.length) return t("orgs.common.requiredFields", {fields: missing.map(([, label]) => label).join(", ")});

    const percent = Number(values.off_percent);
    if (!Number.isInteger(percent) || percent < 0 || percent > 100) {
        return t("orgs.placeEditor.percent");
    }
    return "";
};

const PlaceEditorPage = ({ownerWorkspace = false}) => {
    const {t} = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const {countryScope} = useActiveAdminCountryScope(capabilities);
    const role = capabilities.user?.role;
    const isOwner = ownerWorkspace || role === ADMIN_ROLES.OWNER;
    const placeId = isOwner ? 0 : params.employer_id;
    const isCreate = !isOwner && !placeId;
    const canAssignDeployer = PLATFORM_ROLES.has(role);
    const [values, setValues] = useState(emptyValues);
    const [loadedPlaceKey, setLoadedPlaceKey] = useState(null);
    const [existingMedia, setExistingMedia] = useState({logo: null, agreement: null, background: null});
    const [validationError, setValidationError] = useState("");
    const [mutationResult, setMutationResult] = useState(null);

    const placeQuery = useQuery({
        queryKey: ["admin-place", countryScope, String(placeId)],
        queryFn: () => loadPlace(placeId),
        enabled: !isCreate,
        retry: false,
    });
    const propertiesQuery = useQuery({
        queryKey: ["admin-place-properties", countryScope],
        queryFn: loadPlaceProperties,
        retry: false,
    });

    useEffect(() => {
        if (!placeQuery.data) return;
        setValues(valuesFromPlace(placeQuery.data));
        setExistingMedia({
            logo: placeQuery.data.logo || null,
            agreement: placeQuery.data.agreement || null,
            background: placeQuery.data.background_image || placeQuery.data.background || null,
        });
        setLoadedPlaceKey(String(placeId));
    }, [placeId, placeQuery.data]);

    const saveMutation = useMutation({
        mutationFn: (formData) => isCreate ? createPlace(formData) : updatePlace(placeId, formData),
        onSuccess: (response) => {
            void queryClient.invalidateQueries({queryKey: ["admin-places"]});
            void queryClient.invalidateQueries({queryKey: ["admin-place"]});
            setMutationResult({kind: "success", message: response?.message || t("orgs.placeEditor.saved")});
            if (isOwner) return;
            if (isCreate) {
                const applicationId = response?.application_id;
                if (applicationId) {
                    void queryClient.invalidateQueries({queryKey: ["admin-onboarding"]});
                    navigate(`/business-onboarding/${applicationId}`);
                    return;
                }
                const createdId = response?.id ?? response?.place_id;
                navigate(createdId ? `/employers/${createdId}` : "/employers");
                return;
            }
            navigate(`/employers/${placeId}`);
        },
        onError: (error) => {
            const mapped = mapApiError(error);
            setMutationResult({kind: "error", message: mapped.message});
        },
    });

    if (!capabilities.hasAction("organization.manage")) {
        return <DataState title={t("orgs.common.editUnavailable")} copy={t("orgs.placeEditor.noPermission")} />;
    }
    if (!isCreate && placeQuery.isLoading) {
        return <DataState title={t("orgs.placeEditor.loadingTitle")} copy={t("orgs.placeEditor.loadingCopy")} />;
    }
    if (!isCreate && placeQuery.isError) {
        return <DataState title={t("orgs.placeEditor.errorTitle")} copy={t("orgs.placeEditor.errorCopy")} />;
    }
    if (propertiesQuery.isLoading || (!isCreate && loadedPlaceKey !== String(placeId))) {
        return <DataState title={t("orgs.common.preparingEditor")} copy={t("orgs.placeEditor.preparingCopy")} />;
    }

    const properties = propertiesQuery.data || {};
    const cities = properties.cities || properties.filterCities || [];
    const categories = properties.sub_categories || properties.categories || properties.filterCategories || [];
    const deployers = [...(properties.users || properties.deployers || [])];
    if (values.created_by && !deployers.some((item) => String(item.id) === String(values.created_by))) {
        deployers.push({id: values.created_by, name: values.created_by});
    }
    const placeName = placeQuery.data?.name || values.name;
    const ownerBranches = placeQuery.data?.branches || placeQuery.data?.branch || placeQuery.data?.branch_set || [];
    const ownerPlaceId = placeQuery.data?.id ?? placeId;
    const title = isCreate
        ? t("orgs.placeEditor.createTitle")
        : isOwner
            ? t("orgs.common.changeNamed", {name: placeName || t("orgs.common.placeGenitive")})
            : t("orgs.common.editNamedTitle", {name: placeName || t("orgs.common.placeGenitiveNumber", {id: placeId})});
    const submitLabel = isCreate
        ? (role === ADMIN_ROLES.DEPLOYER || role === ADMIN_ROLES.IMPLEMENTATION_HEAD ? t("orgs.common.sendToModeration") : t("orgs.placeEditor.create"))
        : isOwner ? t("orgs.common.sendRequest") : t("orgs.common.saveChanges");

    const handleChange = (event) => {
        const {name, type, checked, value} = event.target;
        setValidationError("");
        setMutationResult(null);
        setValues((current) => {
            if (name === "logo_missing") {
                return {
                    ...current,
                    logo_exists: !checked,
                    logo_change: true,
                    logo: checked ? null : current.logo,
                };
            }
            return {...current, [name]: type === "checkbox" ? checked : value};
        });
    };

    const handleFileChange = (event) => {
        const {name, files} = event.target;
        const file = files?.[0] || null;
        setValidationError("");
        setMutationResult(null);
        setValues((current) => ({
            ...current,
            [name]: file,
            ...(name === "logo" && file ? {logo_exists: true, logo_change: true} : {}),
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const error = validatePlace(values, {isCreate, existingMedia, t});
        if (error) {
            setValidationError(error);
            return;
        }
        setValidationError("");
        setMutationResult(null);
        saveMutation.mutate(toPlaceFormData(values, {
            mode: isCreate ? "create" : "update",
            includeCreatedBy: canAssignDeployer,
        }));
    };

    return (
        <div className="admin-page organization-editor-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{title}</h1>
                    <p className="admin-page-copy">
                        {isOwner
                            ? t("orgs.placeEditor.ownerCopy")
                            : t("orgs.placeEditor.adminCopy")}
                    </p>
                </div>
                <div className="organization-header-actions">
                    {isOwner && placeQuery.data ? (
                        <Link className="admin-button admin-button-primary" to={`/employers/${placeQuery.data.id ?? 0}/branch/add`}>
                            <GitBranch aria-hidden="true" size={15} /> {t("orgs.common.newBranch")}
                        </Link>
                    ) : null}
                    {!isOwner ? (
                        <Link className="admin-button admin-button-secondary" to="/employers">
                            <ArrowLeft aria-hidden="true" size={15} /> {t("orgs.placeEditor.back")}
                        </Link>
                    ) : null}
                </div>
            </header>
            {propertiesQuery.isError ? (
                <DataState title={t("orgs.common.dictionariesTitle")} copy={t("orgs.placeEditor.dictionariesCopy")} />
            ) : (
                <>
                    <MutationResultBanner result={mutationResult} />
                    <PlaceForm
                        values={values}
                        existingMedia={existingMedia}
                        cities={cities}
                        categories={categories}
                        deployers={deployers}
                        isCreate={isCreate}
                        canAssignDeployer={canAssignDeployer}
                        pending={saveMutation.isPending}
                        validationError={validationError}
                        submitLabel={submitLabel}
                        onChange={handleChange}
                        onFileChange={handleFileChange}
                        onSubmit={handleSubmit}
                    />
                    {isOwner ? (
                        <section className="organization-owner-branches" aria-labelledby="owner-branches-heading">
                            <header>
                                <div>
                                    <h2 id="owner-branches-heading">{t("orgs.common.branches")}</h2>
                                    <p>{t("orgs.placeEditor.branchesCopy")}</p>
                                </div>
                            </header>
                            {ownerBranches.length ? (
                                <div className="organization-owner-branch-list">
                                    {ownerBranches.map((branch) => (
                                        <div className="organization-owner-branch-row" key={branch.id}>
                                            <div>
                                                <Link to={`/employers/${ownerPlaceId}/branch/${branch.id}`}>
                                                    {branch.name || t("orgs.common.branchNumber", {id: branch.id})}
                                                </Link>
                                                <span>
                                                    {[branch.city_name || branch.city, branch.category_name || branch.category]
                                                        .filter(Boolean)
                                                        .join(" · ") || t("orgs.placeEditor.noCityCategory")}
                                                </span>
                                            </div>
                                            <Link
                                                className="admin-button admin-button-secondary"
                                                aria-label={t("orgs.common.editNamed", {name: branch.name || t("orgs.common.branchNumberLower", {id: branch.id})})}
                                                to={`/employers/${ownerPlaceId}/branch/${branch.id}/edit`}
                                            >
                                                <Pencil aria-hidden="true" size={14} /> {t("orgs.common.edit")}
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="admin-muted">{t("orgs.placeEditor.noBranches")}</p>
                            )}
                        </section>
                    ) : null}
                </>
            )}
        </div>
    );
};

export const OrganizationEditorPage = ({mode, ownerWorkspace = false}) => (
    mode === "branch"
        ? <BranchEditorPage />
        : <PlaceEditorPage ownerWorkspace={ownerWorkspace} />
);
